
import React, { useEffect, useState, useMemo, useCallback } from "react";

import tw, { styled, css } from 'twin.macro'

import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { LocalizedLink } from "gatsby-theme-i18n";
import Button from "../Button/Button";
import { Link as GatsbyLink } from "gatsby"
import { useLocalization } from "gatsby-theme-i18n";

import { Handle, Close, AngleDown } from 'ffxiv-fanfest-2024';



import { usePageContext } from "../../context/pageContext/hook";
import HomeAnchor from "../HomeAnchor";



type HeaderProps = {
  theme ?: 'light' | 'dark'
}

type NavItem = {
  label : string
  href : string
  hash ?: string
  badge ?: string
  subNav ?: NavItem[]
}

const Header = ({ theme } : HeaderProps) => {

  const [ isShown, setIsShown ] = useState(false);
  const [ prevPos, setPrevPos ] = useState(0);
  const [ isActive, setIsActive ] = useState(false);
  const [ showMenu, setShowMenu ] = useState(false);
  const [ menuAnimated, setMenuAnimated ] = useState(false);
  const [ currentSubnav, setCurrentSubnav ] = useState([""]);
  const { t } = useLocalization();

  const { pageContext } = usePageContext();

  const {
    header_logo_dark_desktop,
    header_logo_light_desktop

  } = useStaticQuery<GatsbyTypes.SiteHeaderQuery>(graphql`
    query SiteHeaderQuery{
      header_logo_dark_desktop : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "header_logo_dark.png"}) {
          childImageSharp {
            gatsbyImageData(
              placeholder: NONE
              formats: [PNG]
            )
          }
        }
        header_logo_light_desktop : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "header_logo_light.png"}) {
          childImageSharp {
            gatsbyImageData(
              placeholder: NONE
              formats: [PNG]
            )
          }
        }
      }
    `);


    const navList : NavItem[] = useMemo(() => [{
      label : t('common_nav.home.label'),
      href : '/',
      badge : t('common_nav.home.badge.type'),
      hash : 'top'
    },{
      label : t('common_nav.about.label'),
      href : '/about',
      badge : t('common_nav.about.badge.type'),
      subNav : [
        {
          label : t('common_nav.ticket.label'),
          href : '/tickets',
          badge : t('common_nav.ticket.badge.type')
        }
      ]
    },{
      label : t('common_nav.program.label'),
      href : '/program',
      badge : t('common_nav.program.badge.type'),
      subNav : [
        {
          label : t('common_nav.timetable.label'),
          href : '/program/timetable',
          badge : t('common_nav.timetable.badge.type')
        }
      ]
    },{
      label : t('common_nav.activity.label'),
      href : '/activities',
      badge : t('common_nav.activity.badge.type'),
    },{
      label : t('common_nav.floormap.label'),
      href : '/floormap',
      badge : t('common_nav.floormap.badge.type'),
      subNav : [
        {
          label : t('common_nav.floormap_2days.label'),
          href : '/floormap/2days',
          badge : t('common_nav.floormap_2days.badge.type'),
        },
        {
          label : t('common_nav.floormap_viewing.label'),
          href : '/floormap/viewing',
          badge : t('common_nav.floormap_viewing.badge.type'),
        }
      ]
    },{
      label : t('common_nav.contest_campaign.label'),
      href : '/contest_campaign',
      badge : t('common_nav.contest_campaign.badge.type'),
      subNav : [
        {
          label : t('common_nav.fanart_contest.label'),
          href : '/contest_campaign/fanart',
          badge : t('common_nav.fanart_contest.badge.type'),
        }
      ]
    }],[]);



    let updateScroll = useCallback(() => {

      var currentPos = window.scrollY;
      var deltaY = currentPos - prevPos;
      if(currentPos > 100 && !isActive)
      {
        setIsActive(true);
      }
      else if(currentPos <= 100 && isActive)
      {
        setIsActive(false);
      }

      if(deltaY > 0 && currentPos >= 100)
      {
        setIsShown(false);
      }
      else if(deltaY < 0 || currentPos < 100)
      {
        setIsShown(true);
      }
      setPrevPos(currentPos);

    },[isActive,prevPos]);

    useEffect(() => {
      updateScroll();
      window.addEventListener('scroll', updateScroll);

      return function cleanup() {
        window.removeEventListener('scroll', updateScroll);
      };
    });



    const openMenu = () => {
      setShowMenu(true);
      setCurrentSubnav([""]);
      setTimeout(() => {
        setMenuAnimated(true);
      },50);
    };
    const closeMenu = () => {
      setMenuAnimated(false);
      setTimeout(() => {
        setCurrentSubnav([""]);
        setShowMenu(false);
      },400);
    };

    const renderMainNav = useCallback((item : NavItem, index : number) => {
      let subnav = typeof item.subNav !== "undefined" ? <ul className="subnav">
          {item.subNav.map((subItem : NavItem,j) => renderMainNav(subItem,j))}
        </ul> : undefined;

      return <HeaderNav subnav={subnav} badge={item.badge} theme={theme} key={`pc_nav_${index}`}>
        {item.href === "/" ? 
          <HomeAnchor activeClassName="current" isHome={pageContext.namespace === "home"} hash={item.hash} onClick={closeMenu}>
            <HeaderNavAnchor className={item.badge} theme={theme}>
              <span className="label">{item.label}</span>
            </HeaderNavAnchor>
          </HomeAnchor> :
          <HeaderNavAnchor as={GatsbyLink} to={item.href} activeClassName="current" className={item.badge} onClick={closeMenu} theme={theme}>
            <span className="label">{item.label}</span>
          </HeaderNavAnchor>
        }
      </HeaderNav>      
    })


    const renderSideNav = useMemo(() => {
      let sideNav : React.ReactNode[] = [];
      let renderItemSubnav = (navArr : NavItem[],lv : number, navID : string) => {
        let tempNav : React.ReactNode[] = [];
        navArr.forEach((item,i) => {
          tempNav.push(
            <HeaderNavWrapper theme={theme} className={`${typeof item.subNav !== "undefined" ? `withSubnav` : ``}${item.badge ? ` ${item.badge}` : ``}`} key={`res_nav_${navID}_${i}`}>
              {item.href === "/" ? 
                <HomeAnchor activeClassName="current" isHome={pageContext.namespace === "home"} hash={item.hash} onClick={closeMenu}>
                  <HeaderNavAnchor className={item.badge} theme={theme}>
                    <span className="label">{item.label}</span>
                  </HeaderNavAnchor>
                  
                </HomeAnchor> :
                <HeaderNavAnchor as={GatsbyLink} to={item.href} activeClassName="current"  className={item.badge} onClick={closeMenu} theme={theme}>
                  <span className="label">{item.label}</span>
                </HeaderNavAnchor>
              }
              {typeof item.subNav !== "undefined" && 
                <span className="icon"  onClick={(e) => {
                  e.preventDefault();
                  setCurrentSubnav([...currentSubnav,`${navID}_${i}`]);
                }}><AngleDown /></span>
              }
            </HeaderNavWrapper>);
          if(typeof item.subNav !== "undefined")
          {
            renderItemSubnav(item.subNav,lv+1,`${navID}_${i}`);
          }
        })
        
        if(typeof sideNav[lv] === "undefined")
        {
          sideNav[lv] = [];
        }
        sideNav[lv].push(
          <div className={`nav-wrapper ${navID !== '' ? `subnav${navID}` : ''} lv_${lv} ${lv !== 0 ? ` subnav` : ``}${currentSubnav.indexOf(navID) !== -1 ? ' active' : ''}`} id={`${navID !== '' ? `subnav${navID}` : ''}`} key={`res_nav_${navID}`}>
            <nav className={`nav-main`}>
              <ul>{tempNav}</ul>
            </nav>
            {lv === 0 &&
              <nav className="nav-side">
                <HomeAnchor activeClassName="current" isHome={pageContext.namespace === "home"} hash={`inGame_item`} onClick={closeMenu}>
                  <Button theme={theme}>{t('common_nav.ingame_item.label')}</Button>
                </HomeAnchor>
                <Button theme={theme} as={GatsbyLink} to="/goods" activeClassName="current" className={t('common_nav.goods.badge.type')}>{t('common_nav.goods.label')}</Button>
                <HomeAnchor activeClassName="current" isHome={pageContext.namespace === "about"} hash={`streaming`} pagePath={`/about/`} onClick={closeMenu}>
                  <Button theme={theme} className={t('common_nav.stream.badge.type')}>{t('common_nav.stream.label')}</Button>
                </HomeAnchor>
              </nav>
            }
          </div>);
      }

      renderItemSubnav(navList,0,'');
      return sideNav;

    },[currentSubnav]); 





  return (
  <>
  <HeaderWrapper theme={theme} className={`${isActive ? `fixed` : ``} ${!isShown ? `hide`: ``}`}>
    <div className="logo">
      <LocalizedLink to={'/'}>
        <GatsbyImage image={getImage(theme === 'dark' ? header_logo_dark_desktop : header_logo_light_desktop)} alt={'FFXIV Fan Festival 2024 Tokyo'} />
      </LocalizedLink>
    </div>
    <div className="nav">
      <nav className="nav-main">
        <ul>
          {navList.map((item,index) => renderMainNav(item,index))}
        </ul>
      </nav>
      <div className="nav-side">
        <HomeAnchor activeClassName="current" isHome={pageContext.namespace === "home"} hash={`inGame_item`} onClick={closeMenu}>
          <Button theme={theme}>{t('common_nav.ingame_item.label')}</Button>
        </HomeAnchor>
        <Button theme={theme} as={GatsbyLink} to="/goods" activeClassName="current" className={t('common_nav.goods.badge.type')}>{t('common_nav.goods.label')}</Button>
        <HomeAnchor activeClassName="current" isHome={pageContext.namespace === "about"} hash={`streaming`} pagePath={`/about/`}onClick={closeMenu}>
          <Button theme={theme} className={t('common_nav.stream.badge.type')}>{t('common_nav.stream.label')}</Button>
        </HomeAnchor>
      </div>
      <div className="nav-mobile">
        <a href="#" onClick={(e) => {
          e.preventDefault();
          openMenu();
        }}>
          <Handle title={t('common_nav.menu.button.label')}/>
        </a>
      </div>
    </div>

  </HeaderWrapper>
  <SidebarWrapper className={`${showMenu ? `active` : ``}${menuAnimated ? ` animated` : ``}`}>
    <div className="sidebar">
      {currentSubnav.length > 1 &&
        <a href="#" className="toggle btnBack" onClick={(e) => {
            e.preventDefault();
            setCurrentSubnav(currentSubnav.slice(0, -1));
        }}>
          <AngleDown title={'back'}/>
        </a>
      }
      
      <a href="#" className="toggle" onClick={(e) => {
          e.preventDefault();
          closeMenu();
      }}>
        <Close title={t('common_nav.close.button.label')}/>
      </a>
      {renderSideNav}
    </div>
    <div className="overlay" onClick={(e) => {
          e.preventDefault();
          closeMenu();
      }}>

    </div>
  </SidebarWrapper>

  </>)
}

type HeaderNavProps = {
  children ?: React.ReactNode
  subnav ?: React.ReactNode
  badge ?: string
  theme ?: string
}

const HeaderNav = ({ children, subnav, badge, theme } : HeaderNavProps) => {
  const [ isShowSubnav, setIsShowSubnav] = useState(false);

  return(
    <HeaderNavWrapper  theme={theme} className={`${typeof subnav !== "undefined" ? `withSubnav` : ``}${badge ? ` ${badge}` : ``}${isShowSubnav ? ` showSubnav` : ``}`}  onClick={(e) => {
        e.preventDefault();
        setIsShowSubnav(false);
      }} >
      {children}
      {typeof subnav !== "undefined" && 
      <>
        <span className="icon"  onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsShowSubnav(!isShowSubnav);
        }}><AngleDown /></span>
        <div className="subnav-overlay" onClick={(e) => {
          e.preventDefault();
          setIsShowSubnav(false);
        }}></div>
      </>}
      {subnav}
    </HeaderNavWrapper >


  )
}


const HeaderNavWrapper = styled.li`
  ${tw`
    relative

    after:absolute
    after:hidden
    after:w-[6px]
    after:h-[6px]
    after:rounded-full
    after:top-2
    after:right-2   
  `}

  &.withSubnav{
    ${tw`
      flex
      pr-4
      rounded-[6px 6px 0px 0px]

      lg:pr-3
    `}
    .subnav-overlay{
      ${tw`
        fixed
        z-[99]
        inset-0
        bg-black
        opacity-0
        hidden
      `}
    }
    
    &:hover, &.showSubnav{

      > a, > span{
        ${tw`
          z-[100]
        `}
      }

      ${(props : HeaderProps) => props.theme === 'dark' ? tw`
        bg-livid-900
      ` : tw`
        bg-livid-50
      `}

      > .subnav{
        ${tw`block`}
      }

      span.icon > span{
        .subnav{
          ${tw`
            rotate-[180deg]
          `}
        }
      }
    }

    &.showSubnav .subnav-overlay{
      ${tw`
        block
      `}
    }
  }
  

  span.icon{
    ${tw`
      inline-block
      align-top
      my-4
      w-6
      h-6
      text-[24px]
      ml-1
      cursor-pointer

      lg:my-3
      md:my-3
      sm:my-3
    `}

    > span{
      ${tw`
        transition-transform
      `}
    }
  }

  ${(props : HeaderProps) => props.theme === 'dark' ?
  tw`
    text-livid-200
    active:text-livid-50
  ` :
  tw`
    text-livid-800
    active:text-livid-900
  `}

  &.update{
    ${tw`
      after:inline-block
    `}

    ${(props : HeaderProps) => props.theme === 'dark' ? tw`
      after:bg-badge-updated-dark
    ` : tw`
      after:bg-badge-updated-light
    `}
  }

  &.new{
    ${tw`
      after:inline-block
    `}

    ${(props : HeaderProps) => props.theme === 'dark' ? tw`
      after:bg-badge-new-dark
    ` : tw`
      after:bg-badge-new-light
    `}
  }

  .subnav &, .sidebar &{
    ${tw`
      after:top-2
      after:right-auto
      after:left-2
    `}

    span.icon > span, &:hover span.icon > span{
      ${tw`
        rotate-[-90deg]
      `}
    }
  }

  

  &,&.withSubnav{
    ${tw`
      md:hidden
      sm:hidden
    `}
  }

  .sidebar &{
    ${tw`
      md:block
      sm:block
    `}

    span.icon{
      ${tw`
        absolute
        right-0
        top-0
      `}
    }

    .subnav{
      ${tw`
        translate-x-[100%]
        transition-transform
      `}
    }

    &.showSubnav .subnav{
      ${tw`
        translate-x-[0]
      `}
    }
    
  }
  

`

const HeaderNavAnchor = styled.a`
  ${tw`
    block
    px-4
    font-bold
    text-[13px]
    leading-[16px]
    relative
    cursor-pointer
    
    lg:px-3
    lg:text-[12px]

    
  `}

  .withSubnav &{
    ${tw`
      pr-1
    `}
  }

  span.label{
    ${tw`
      inline-block
      py-5
      relative       
      before:absolute
      before:block
      before:left-0
      before:bottom-0
      before:w-0
      before:h-[3px]
      before:rounded-[2px]
      before:transition-[width]

      lg:py-4
    `}

    ${(props : HeaderProps) => props.theme === 'dark' ?
    tw`
      before:bg-livid-400
    ` :
    tw`
      before:bg-livid-600        
    `}
  }

  

  &:hover > span.label{
    ${tw`
      before:w-full
    `}
  }

  &:active > span.label{
    ${(props : HeaderProps) => props.theme === 'dark' ?
    tw`
      before:bg-livid-50
    ` :
    tw`
      before:bg-livid-900
    `}
  }


  ${(props : HeaderProps) => props.theme === 'dark' ?
  tw`
    text-livid-200
    active:text-livid-50
  ` :
  tw`
    text-livid-800
    active:text-livid-900
  `}

  &.current{
    > span.label{
      ${tw`
        before:w-full
      `}
      ${(props : HeaderProps) => props.theme === 'dark' ?
      tw`
        before:bg-fanfes-dark-500
      ` :
      tw`
        before:bg-fanfes-light-800
      `}
    }

    ${(props : HeaderProps) => props.theme === 'dark' ?
    tw`
      text-livid-50
    ` :
    tw`
      text-livid-900
    `}
  }

  &.disabled{
    ${tw`
      opacity-40
      before:w-0
    `}
  }



  .subnav &, .sidebar &{
    ${tw`
      p-4
    `}

    > span.label{
     ${tw`
        py-0
        before:absolute
        before:block
        before:left-[-19px]
        before:top-0
        before:w-[3px]
        before:h-0
        before:rounded-[2px]
        before:transition-[height]
     `} 
    }

    &:hover > span.label, &.current > span.label{
      ${tw`
        before:h-full
        before:w-[3px]
      `}
    }


  }

  


`

const HeaderWrapper = styled.header<HeaderProps>`
  ${tw`
    flex
    fixed
    py-8
    px-16
    w-full
    items-center
    justify-between
    z-20
    transition-[background-color, transform]
    duration-[500ms]

    lg:py-6
    lg:px-12

    md:py-6
    md:px-8

    sm:py-4
    sm:px-6
  `}

  .logo{
    a{
    ${tw`
      block
      w-[173px]
      h-[112px]
      lg:w-[111px]
      lg:h-[72px]

      md:w-[111px]
      md:h-[72px]

      sm:w-[87px]
      sm:h-[56px]
    `}

    .gatsby-image-wrapper{
      ${tw`
        w-full
        h-full
      `}
    }
    }
  }

  &.fixed{
    background-color: rgba(44, 44, 46, 0.50);
    backdrop-filter: blur(4px);
  }

  &.hide{
    ${tw`
      translate-y-[-100%]
    `}
  }

  .nav{
    ${tw`
      flex
      gap-8

      lg:gap-2
      md:gap-4
    `}

  }

  .nav-main ul{
    ${tw`
      flex
    `}

    li{
      ${tw`
        relative
      `}
    }

    .subnav{
      ${tw`
        w-[285px]
        shadow-[0px 24px 16px -16px rgba(0, 0, 0, 0.05), 0px 12px 8px -8px rgba(0, 0, 0, 0.05), 0px 6px 4px -4px rgba(0, 0, 0, 0.10)]
        absolute
        hidden
        top-[100%]
        rounded-[0px 6px 12px 12px]
        p-[24px 16px]
      `}



      ${(props : HeaderProps) => props.theme === 'dark' ? tw`
        bg-livid-900
      ` : tw`
        bg-livid-50
      `}
    }

    
  }

  .nav-side{
    ${tw`
      flex
      gap-2
      sm:hidden
    `}
  }

  .nav-mobile{
    ${tw`
      hidden
      md:block
      sm:block
    `}

    a{
      ${tw`
        flex
        items-center
        justify-center
        w-[46px]
        h-[46px]
        border-2
        border-transparent
        text-livid-200
        border-solid
        transition-colors
        rounded-[6px]
      `}

      span[class*=icon-]
      {
        ${tw`
          w-8
          h-8

          text-[32px]
        `}
      }

      &:hover{
        ${tw`
          border-livid-50
          text-livid-50
        `}
      }

      &:active, &.active{
        ${tw`
          border-livid-50
          bg-livid-50
          text-livid-900
        `}
      }
    }


  }

`

const SidebarWrapper = styled.div`
  ${tw`
    h-screen
    w-[368px]
    fixed
    right-0
    top-0
    z-[100]
    hidden

    sm:w-[272px]

  `}



  .overlay{
    ${tw`
      absolute
      w-screen
      h-screen
      bg-black
      opacity-0
      right-0
      top-0

      transition-[opacity]
      duration-[400ms]
    `}
  }

  .toggle{
      ${tw`
        flex
        items-center
        justify-center
        w-[46px]
        h-[46px]
        border-2
        border-transparent
        text-livid-200
        border-solid
        transition-colors
        rounded-[6px]
        absolute
        top-8
        right-8
        z-[999]

        sm:top-5
        sm:right-6

      `}

      &.btnBack{
        ${tw`
          right-auto
          left-8
          rotate-[90deg]
          sm:left-6
        `}
      }

      span[class*=icon-]
      {
        ${tw`
          w-8
          h-8

          text-[32px]
        `}
      }

      &:hover{
        ${tw`
          border-livid-50
          text-livid-50
        `}
      }

      &:active, &.active{
        ${tw`
          border-livid-50
          bg-livid-50
          text-livid-900
        `}
      }

  }

  .sidebar{
    ${tw`
      absolute
      inset-0
      bg-livid-900
      z-10
      overflow-x-hidden
      overflow-y-auto


      translate-x-[100%]

      transition-[transform]
      duration-[400ms]
    `}

    .nav-wrapper{
      ${tw`
        min-h-full
        flex
        flex-col
        justify-center
        pt-[68px]
        pb-[24px]
      `}

      
    
      &.subnav{
        ${tw`
          absolute
          w-full
          translate-x-[100%]
          transition-transform
          bg-livid-900
          top-0
          left-0
          z-[101]
        `}

        &.active{
          ${tw`
            translate-x-0
          `}
        }
      }
    }

    

    .nav-main{
      ${tw`
        flex
        flex-col
        px-12
        gap-2
        w-full

        sm:px-6
      `}

      .subnav{
        ${tw`
          fixed
          w-full
          h-full
          top-0
          right-0
          bg-livid-900
          z-[101]
          flex
          items-center
          px-12
        `}
      }
    }

    .nav-side{
      ${tw`
        mt-4
        px-12
        flex
        flex-col
        gap-2
        w-full

        sm:px-6
      `}

      a{
        ${tw`
          w-full
          text-center
        `}
      }
    }
  }

  &.active{
    ${tw`
      block
      lg:hidden
      xl:hidden
    `}


  }

  &.animated{
    .overlay{
      ${tw`
        opacity-50
      `}
    }

    .sidebar{
      ${tw`
        translate-x-0
      `}
    }
  }


`

export default Header;
